import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import {
  fetchDispenseMaterials,
  updateDispenseMaterial,
  createDispenseMaterial,
  seedDispenseMaterials
} from "../../../slices/company/dispenseMaterialSlice";
import { Pen, Printer, RefreshCw, Info, Plus } from "lucide-react";
import { customTableStyles } from "../../../utils/tableStylings";
import { API_URL } from "../../../utils/constant";
import Modal from "../../../components/modal";

const MATERIAL_TYPES = [
  { key: "bottle", label: "Bottle" },
  { key: "white_cap", label: "White Cap" },
  { key: "seal_cap", label: "Seal Cap" },
  { key: "dropper", label: "Dropper" },
  { key: "push", label: "Push" },
  { key: "label", label: "Label" },
  { key: "globules", label: "Globules Base" },
  { key: "alcohol", label: "Alcohol / Spirit" },
  { key: "water", label: "Distilled Water" },
  { key: "puria", label: "Puria / Paper Packet" }
];

const BOTTLE_TYPES = ["Regular", "L"];
const SIZE_OPTIONS = ["5ML", "10ML", "15ML", "20ML", "30ML", "60ML", "100ML"];
const CAP_NO_OPTIONS = ["No. 1", "No. 2", "No. 3", "No. 4"];

const DispensingMaterials = () => {
  const dispatch = useDispatch();
  const { materials, loading } = useSelector((state) => state.dispenseMaterial);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  // Form states
  const [nameInput, setNameInput] = useState("");
  const [skuInput, setSkuInput] = useState("");
  const [unitInput, setUnitInput] = useState("PCS");
  const [qtyInput, setQtyInput] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [costPriceInput, setCostPriceInput] = useState("");
  const [matType, setMatType] = useState("bottle");
  const [btlType, setBtlType] = useState("Regular");
  const [sizeVal, setSizeVal] = useState("30ML");
  const [numberVal, setNumberVal] = useState("No. 1");

  const company = JSON.parse(localStorage.getItem("company"));
  const companyId = company?.company?.id;

  useEffect(() => {
    if (companyId) {
      dispatch(fetchDispenseMaterials(companyId));
    }
  }, [dispatch, companyId]);

  const handleSeed = () => {
    if (!companyId) return;
    Swal.fire({
      title: "<strong>Seed Dispensing Materials?</strong>",
      html: `<p>This will seed all size variations for Bottles, Droppers, Labels, Caps (No. 1-4), Alcohol, Globules, and Purias into your dedicated dispense materials inventory.</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Seed Now",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(seedDispenseMaterials(companyId)).then((action) => {
          if (action.meta.requestStatus === "fulfilled") {
            dispatch(fetchDispenseMaterials(companyId));
            Swal.fire({
              icon: "success",
              title: "Seeded!",
              text: "Dispensing Materials seeded successfully.",
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            });
          } else {
            Swal.fire("Error", "Failed to seed dispensing materials.", "error");
          }
        });
      }
    });
  };

  const handleAdd = () => {
    setNameInput("");
    setSkuInput("");
    setUnitInput("PCS");
    setQtyInput("100");
    setPriceInput("15");
    setCostPriceInput("10");
    setMatType("bottle");
    setBtlType("Regular");
    setSizeVal("30ML");
    setNumberVal("No. 1");
    setShowAddModal(true);
  };

  const handleEdit = (item) => {
    setSelectedMaterial(item);
    setNameInput(item.name || "");
    setSkuInput(item.sku || "");
    setUnitInput(item.unit || "PCS");
    setQtyInput((item.quantity || 0).toString());
    setPriceInput((item.price || 0).toString());
    setCostPriceInput((item.cost_price || 0).toString());
    setMatType(item.material_type || "bottle");
    setBtlType(item.bottle_type || "Regular");
    setSizeVal(item.size_val || "30ML");
    setNumberVal(item.number_val || "No. 1");
    setShowEditModal(true);
  };

  const handleCreate = async () => {
    if (!nameInput.trim()) {
      Swal.fire("Validation Error", "Material Name is required", "error");
      return;
    }
    try {
      await dispatch(
        createDispenseMaterial({
          companyId: companyId,
          materialData: {
            name: nameInput,
            sku: skuInput,
            unit: unitInput,
            quantity: parseFloat(qtyInput) || 0,
            price: parseFloat(priceInput) || 0,
            cost_price: parseFloat(costPriceInput) || 0,
            material_type: matType,
            bottle_type: matType === 'bottle' ? btlType : null,
            size_val: ['bottle', 'dropper', 'label'].includes(matType) ? sizeVal : null,
            number_val: ['white_cap', 'seal_cap', 'push'].includes(matType) ? numberVal : null
          }
        })
      ).unwrap();

      dispatch(fetchDispenseMaterials(companyId));
      setShowAddModal(false);
      Swal.fire({
        icon: "success",
        title: "Created",
        text: "Dispensing material created successfully.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (err) {
      Swal.fire("Error", "Failed to create material.", "error");
    }
  };

  const handleSave = async () => {
    if (!selectedMaterial) return;
    try {
      await dispatch(
        updateDispenseMaterial({
          id: selectedMaterial.id,
          materialData: {
            name: nameInput,
            sku: skuInput,
            unit: unitInput,
            quantity: parseFloat(qtyInput) || 0,
            price: parseFloat(priceInput) || 0,
            cost_price: parseFloat(costPriceInput) || 0,
            material_type: matType,
            bottle_type: matType === 'bottle' ? btlType : null,
            size_val: ['bottle', 'dropper', 'label'].includes(matType) ? sizeVal : null,
            number_val: ['white_cap', 'seal_cap', 'push'].includes(matType) ? numberVal : null
          },
        })
      ).unwrap();

      dispatch(fetchDispenseMaterials(companyId));
      setShowEditModal(false);
      Swal.fire({
        icon: "success",
        title: "Updated",
        text: "Stock and prices updated successfully.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (err) {
      Swal.fire("Error", "Failed to update material.", "error");
    }
  };

  const handlePrintBarcode = (barcodeImage, barcode) => {
    if (barcodeImage) {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Popup blocked! Please allow popups for printing.");
        return;
      }
      printWindow.document.write(`
        <html>
          <head>
            <title>Print Barcode</title>
            <style>
              @media print {
                @page { margin: 0; }
                body { margin: 0; margin-left: 15px; }
              }
              body { margin: 0; padding: 0; margin-left: 15px; }
              .barcode-container {
                width: 50mm;
                height: 22mm;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                font-size: 12px;
                page-break-after: always;
              }
              .barcode-container img {
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 70%;
              }
            </style>
          </head>
          <body>
            <div class="barcode-container">
              <img src="${API_URL}${barcodeImage}" onload="window.print()" alt="${barcode}" />
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
    }
  };

  const columns = [
    {
      name: "Material Name",
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
    },
    {
      name: "Type",
      selector: (row) => MATERIAL_TYPES.find(t => t.key === row.material_type)?.label || row.material_type || "N/A",
      sortable: true,
    },
    {
      name: "Attributes",
      cell: (row) => {
        const parts = [];
        if (row.bottle_type) parts.push(`Type: ${row.bottle_type}`);
        if (row.size_val) parts.push(`Size: ${row.size_val}`);
        if (row.number_val) parts.push(`No: ${row.number_val}`);
        return parts.length > 0 ? parts.join(", ") : "-";
      },
      sortable: true,
    },
    {
      name: "Stock Qty",
      selector: (row) => `${row.quantity} ${row.unit}`,
      sortable: true,
    },
    {
      name: "Cost Price",
      cell: (row) => `₹${(parseFloat(row.cost_price) || 0).toFixed(2)}`,
      sortable: true,
    },
    {
      name: "MRP / Price",
      cell: (row) => `₹${(parseFloat(row.price) || 0).toFixed(2)}`,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(row)}>
            <Pen size={14} />
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handlePrintBarcode(row.barcode_image, row.barcode)}
          >
            <Printer size={14} />
          </button>
        </div>
      ),
      width: "120px",
    },
  ];

  return (
    <div className="row">
      <div className="col-md-12">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="text-primary mb-0 fw-bold">Dispensing Materials & Packaging</h4>
            <p className="text-muted mb-0">Manage dedicated inventory and prices of bottles, spirit, globules, caps, and purias</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={() => handleSeed()}>
              <RefreshCw size={16} className="me-1" /> Seed Standard Materials
            </button>
            <button className="btn btn-primary" onClick={() => handleAdd()}>
              <Plus size={16} className="me-1" /> Add New Material
            </button>
          </div>
        </div>

        {/* Excel Reference Notes banner */}
        <div className="alert alert-info d-flex align-items-start gap-2 mb-4" role="alert">
          <Info size={18} className="mt-1 flex-shrink-0" />
          <div>
            <h6 className="alert-heading fw-bold mb-1">Dedicated Dispensing Materials Table Active</h6>
            <p className="mb-0">
              You can now track inventory of specific sizes (e.g. 5ml dropper, 10ml dropper, regular bottle 30ml) separately. Select the type, size, and cap number to allow the POS checkout to deduct stock accurately.
            </p>
          </div>
        </div>

        {/* Datatable */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            <DataTable
              columns={columns}
              data={materials}
              loading={loading}
              customStyles={customTableStyles}
              pagination
              paginationPerPage={15}
              paginationRowsPerPageOptions={[10, 15, 20, 30]}
              highlightOnHover
            />
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <Modal show={showAddModal} onClose={() => setShowAddModal(false)} heading="Add Dispensing Material">
        <div className="modal-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Material Name</label>
              <input
                type="text"
                className="form-control"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="e.g. White Cap No. 1"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">SKU / Barcode</label>
              <input
                type="text"
                className="form-control"
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value)}
                placeholder="Auto-generated if left blank"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Material Type</label>
              <select className="form-select" value={matType} onChange={(e) => setMatType(e.target.value)}>
                {MATERIAL_TYPES.map(t => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Unit</label>
              <select className="form-select" value={unitInput} onChange={(e) => setUnitInput(e.target.value)}>
                <option value="PCS">PCS</option>
                <option value="ML">ML</option>
                <option value="GM">GM</option>
              </select>
            </div>
          </div>

          {/* Conditional Attributes */}
          {matType === 'bottle' && (
            <div className="mb-3">
              <label className="form-label fw-bold">Bottle Type</label>
              <select className="form-select" value={btlType} onChange={(e) => setBtlType(e.target.value)}>
                {BOTTLE_TYPES.map(bt => (
                  <option key={bt} value={bt}>{bt} Bottle</option>
                ))}
              </select>
            </div>
          )}

          {['bottle', 'dropper', 'label'].includes(matType) && (
            <div className="mb-3">
              <label className="form-label fw-bold">Volume Size</label>
              <select className="form-select" value={sizeVal} onChange={(e) => setSizeVal(e.target.value)}>
                {SIZE_OPTIONS.map(sz => (
                  <option key={sz} value={sz}>{sz}</option>
                ))}
              </select>
            </div>
          )}

          {['white_cap', 'seal_cap', 'push'].includes(matType) && (
            <div className="mb-3">
              <label className="form-label fw-bold">Number (Size/No)</label>
              <input
                type="text"
                className="form-control"
                value={numberVal}
                onChange={(e) => setNumberVal(e.target.value)}
                placeholder="e.g. No. 1, 22 no"
              />
            </div>
          )}

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">Stock Quantity</label>
              <input
                type="number"
                className="form-control"
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">Cost Price (₹)</label>
              <input
                type="number"
                className="form-control"
                value={costPriceInput}
                onChange={(e) => setCostPriceInput(e.target.value)}
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">MRP / Price (₹)</label>
              <input
                type="number"
                className="form-control"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate}>Create Material</button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)} heading={`Edit Material: ${selectedMaterial?.name}`}>
        <div className="modal-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Material Name</label>
              <input
                type="text"
                className="form-control"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">SKU / Barcode</label>
              <input
                type="text"
                className="form-control"
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value)}
                disabled
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Material Type</label>
              <select className="form-select" value={matType} onChange={(e) => setMatType(e.target.value)}>
                {MATERIAL_TYPES.map(t => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">Unit</label>
              <select className="form-select" value={unitInput} onChange={(e) => setUnitInput(e.target.value)}>
                <option value="PCS">PCS</option>
                <option value="ML">ML</option>
                <option value="GM">GM</option>
              </select>
            </div>
          </div>

          {/* Conditional Attributes */}
          {matType === 'bottle' && (
            <div className="mb-3">
              <label className="form-label fw-bold">Bottle Type</label>
              <select className="form-select" value={btlType} onChange={(e) => setBtlType(e.target.value)}>
                {BOTTLE_TYPES.map(bt => (
                  <option key={bt} value={bt}>{bt} Bottle</option>
                ))}
              </select>
            </div>
          )}

          {['bottle', 'dropper', 'label'].includes(matType) && (
            <div className="mb-3">
              <label className="form-label fw-bold">Volume Size</label>
              <select className="form-select" value={sizeVal} onChange={(e) => setSizeVal(e.target.value)}>
                {SIZE_OPTIONS.map(sz => (
                  <option key={sz} value={sz}>{sz}</option>
                ))}
              </select>
            </div>
          )}

          {['white_cap', 'seal_cap', 'push'].includes(matType) && (
            <div className="mb-3">
              <label className="form-label fw-bold">Number (Size/No)</label>
              <input
                type="text"
                className="form-control"
                value={numberVal}
                onChange={(e) => setNumberVal(e.target.value)}
                placeholder="e.g. No. 1, 22 no"
              />
            </div>
          )}

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">Stock Quantity</label>
              <input
                type="number"
                className="form-control"
                value={qtyInput}
                onChange={(e) => setQtyInput(e.target.value)}
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">Cost Price (₹)</label>
              <input
                type="number"
                className="form-control"
                value={costPriceInput}
                onChange={(e) => setCostPriceInput(e.target.value)}
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">MRP / Price (₹)</label>
              <input
                type="number"
                className="form-control"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>Save Changes</button>
        </div>
      </Modal>
    </div>
  );
};

export default DispensingMaterials;
